import {
  Box,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumberFormatValues, NumericFormat } from "react-number-format";
import FormSection from "../../../common/components/functional/formSection/FormSection";
import isEqual from "../../../common/helpers/isEqual";
import usePrevious from "../../../hooks/usePrevious";
import { ShiftDetails2 } from "../orderAPI";
import { ValidationError } from "../orderTypes";
import { validateNotificationTime } from "../utils";
import { ShiftDetailsType } from "./types";

type Props = {
  onTeamNotificationSettingsChange: (
    name: "notifyWhenFulfilled" | "notifyMinutesBeforeIfNotFulfilled",
    value: boolean | number,
  ) => void;
  validationErrors?: ValidationError[];
  fieldId?: string;
  shiftDetails?: ShiftDetailsType[] | ShiftDetails2[];
  handleError?: (error: boolean, validationError: ValidationError) => void;
  notifyWhenFulfilled?: boolean;
  notifyMinutesBeforeIfNotFulfilled?: number;
  disableEdit?: boolean;
  isEditing?: boolean;
};

const setInitialHours = (minutes: number) => {
  return minutes > 0 ? (minutes / 60).toString() : "";
};

const TeamNotificationSettings = ({
  onTeamNotificationSettingsChange,
  validationErrors = [],
  fieldId = "",
  shiftDetails = [],
  handleError,
  notifyWhenFulfilled = false,
  notifyMinutesBeforeIfNotFulfilled = 0,
  disableEdit = false,
  isEditing = false,
}: Props) => {
  const [hoursChecked, setHoursChecked] = useState(
    !!notifyMinutesBeforeIfNotFulfilled,
  );
  const [hoursValue, setHoursValue] = useState<string>(
    setInitialHours(notifyMinutesBeforeIfNotFulfilled),
  );
  const [isDirty, setIsDirty] = useState(false);
  const previousShiftDetails = usePrevious(shiftDetails);

  const { t } = useTranslation("order");

  // Reset errors when component mounts and clean up when unmounts
  useEffect(() => {
    // Mount: reset errors if field is empty/unchecked
    if (handleError && !hoursChecked && !hoursValue.trim()) {
      handleError(false, {
        id: fieldId,
        field: "notifyMinutesBeforeIfNotFulfilled",
        errorType: "any",
      });
    }

    // Unmount: clean up errors
    return () => {
      if (handleError) {
        handleError(false, {
          id: fieldId,
          field: "notifyMinutesBeforeIfNotFulfilled",
          errorType: "any",
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Change isDirty when shiftDetails changes in edit mode
  useEffect(() => {
    if (
      isEditing &&
      previousShiftDetails &&
      !isEqual(shiftDetails, previousShiftDetails)
    ) {
      setIsDirty(true);
    }
  }, [shiftDetails, isEditing, previousShiftDetails]);

  // Re-validate when shiftDetails changes during editing
  useEffect(() => {
    if (hoursChecked && handleError && hoursValue.trim()) {
      const numValue = Number(hoursValue);
      const timeValidation = validateNotificationTime(numValue, shiftDetails);

      // In edit mode, only validate if user has actually changed something
      const shouldValidate = isEditing ? isDirty : true;

      if (shouldValidate) {
        if (!timeValidation.isValid) {
          handleError(true, {
            id: fieldId,
            field: "notifyMinutesBeforeIfNotFulfilled",
            errorType: timeValidation.errorType || "notificationTimePassed",
          });
        } else {
          handleError(false, {
            id: fieldId,
            field: "notifyMinutesBeforeIfNotFulfilled",
            errorType: "any",
          });
        }
      }
    }
  }, [
    shiftDetails,
    isEditing,
    isDirty,
    hoursChecked,
    hoursValue,
    handleError,
    fieldId,
    notifyMinutesBeforeIfNotFulfilled,
  ]);

  const isValidHours = (hours: string) => {
    return hours.trim() !== "" && Number(hours) >= 1 && Number(hours) <= 999;
  };

  const isAllowed = (values: NumberFormatValues) => {
    const { floatValue } = values;
    return floatValue === undefined || isValidHours(floatValue.toString());
  };

  const onHoursChange = (value: string) => {
    setHoursValue(value);
    setIsDirty(true);

    const numValue = Number(value) || 0;
    const minutes = numValue && numValue * 60;
    onTeamNotificationSettingsChange(
      "notifyMinutesBeforeIfNotFulfilled",
      minutes,
    );

    // Validate when checkbox is checked
    if (hoursChecked && handleError) {
      // Check if field is empty (required validation)
      if (!value.trim()) {
        handleError(true, {
          id: fieldId,
          field: "notifyMinutesBeforeIfNotFulfilled",
          errorType: "requiredHours",
        });
        return;
      }

      // Validate against shift time
      const numValue = Number(value);
      const timeValidation = validateNotificationTime(numValue, shiftDetails);
      if (!timeValidation.isValid) {
        handleError(true, {
          id: fieldId,
          field: "notifyMinutesBeforeIfNotFulfilled",
          errorType: timeValidation.errorType || "notificationTimePassed",
        });
        return;
      }

      // Clear any existing errors
      handleError(false, {
        id: fieldId,
        field: "notifyMinutesBeforeIfNotFulfilled",
        errorType: "any",
      });
    }
  };

  const onHoursCheckboxChange = (checked: boolean) => {
    setHoursChecked(checked);
    setIsDirty(true);

    // Clear validation error and hours when unchecking
    if (!checked && handleError) {
      setHoursValue("");
      handleError(false, {
        id: fieldId,
        field: "notifyMinutesBeforeIfNotFulfilled",
        errorType: "requiredHours",
      });
    }

    // Validate when checking
    if (checked && handleError) {
      // Check if field is empty (required validation)
      if (!hoursValue.trim()) {
        handleError(true, {
          id: fieldId,
          field: "notifyMinutesBeforeIfNotFulfilled",
          errorType: "requiredHours",
        });
        return;
      }

      // Validate against shift time
      const numValue = Number(hoursValue);
      const timeValidation = validateNotificationTime(numValue, shiftDetails);
      if (!timeValidation.isValid) {
        handleError(true, {
          id: fieldId,
          field: "notifyMinutesBeforeIfNotFulfilled",
          errorType: timeValidation.errorType || "notificationTimePassed",
        });
        return;
      }

      // Clear any existing errors
      handleError(false, {
        id: fieldId,
        field: "notifyMinutesBeforeIfNotFulfilled",
        errorType: "any",
      });
    }
  };

  const onFulfilledCheckboxChange = (checked: boolean) =>
    onTeamNotificationSettingsChange("notifyWhenFulfilled", checked);

  const error = validationErrors.find(
    (error) =>
      error.id === fieldId &&
      error.field === "notifyMinutesBeforeIfNotFulfilled",
  );

  const helperText = error ? t(error.errorType) : t("notificationHelperText");

  const NotifyMinutesBeforeIfNotFulfilledLabel = (
    <Box display="flex" alignItems="center" gap="6px">
      <Typography>{t("notifyMinutesBeforeIfNotFulfilledPrefix")}</Typography>
      <NumericFormat
        value={hoursValue}
        onValueChange={(values) => onHoursChange(values.value)}
        disabled={!hoursChecked || disableEdit}
        isAllowed={isAllowed}
        allowNegative={false}
        decimalScale={0}
        valueIsNumericString
        customInput={TextField}
        size="small"
        sx={{ width: "64px" }}
        error={!!error}
        inputProps={{
          style: { textAlign: "center" },
        }}
      />
      <Typography>{t("notifyMinutesBeforeIfNotFulfilledSuffix")}</Typography>
    </Box>
  );

  return (
    <FormSection noBorder title={t("orderNotifications")}>
      <>
        <Grid container item xs={12} ml="12px">
          <FormControlLabel
            disabled={disableEdit}
            onChange={(_, checked) => onFulfilledCheckboxChange(checked)}
            control={<Checkbox checked={notifyWhenFulfilled} />}
            label={t("notifyWhenFulfilledLabel")}
          />
        </Grid>
        <Grid container item xs={12} ml="12px" direction="column">
          <FormControlLabel
            disabled={disableEdit}
            onChange={(_, checked) => onHoursCheckboxChange(checked)}
            control={<Checkbox checked={hoursChecked} />}
            label={NotifyMinutesBeforeIfNotFulfilledLabel}
          />
          <FormHelperText error={!!error} sx={{ marginLeft: "8px" }}>
            {helperText}
          </FormHelperText>
        </Grid>
      </>
    </FormSection>
  );
};

export default TeamNotificationSettings;
